import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import CheckoutSectionContent from './utils/Checkout.SectionContent';
import CheckoutSectionFooter from './utils/Checkout.SectionFooter';
import { translate } from '../../Services/translation';
import {
  setCustomerDetails,
  setAlternativeAddress,
  setShowAlternativeAddress,
  setSignUp,
  setValidationErrors,
} from '../../Actions/Checkout.action';
import { privateCustomerAddressSchema } from './utils/Checkout.ValidationSchema';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Dropdown from '../Dropdown';
import styled from 'styled-components';
import Checkbox from '../Inputs/Checkbox';
import Input from '../Inputs/Input';
import { InputProps } from '../Inputs/types/Input.types';
import { StyledPrivateCustomerWrapper } from './styles/Checkout.PrivateCustomerInfo.styling';
import { text } from '../../styling/mixins/typography';
import { getColor } from '../../styling/mixins/colors';

interface CustomerInfoInputProps {
  validationSchema: any;
  stateKey: string;
  id: string;
  onCustomerDetailsChange?: (
    stateKey: string,
    id: string,
    value: string
  ) => void;
}

const StyledLoginLink = styled.div`
  ${text('medium')}

  a {
    color: ${getColor('grey', 900)};
    text-decoration: underline;
  }
`;

const CustomerInfoInput: React.FC<
  CustomerInfoInputProps &
    InputProps &
    React.InputHTMLAttributes<HTMLInputElement>
> = ({
  validationSchema,
  stateKey,
  onCustomerDetailsChange,
  id,
  wrapperClassName,
  ...props
}) => {
  const dispatch = useDispatch();
  const { validationErrors = {}, payload } = useTypedSelector(
    (state) => state.checkout
  );

  const errorMessage = useMemo(
    () =>
      validationErrors[`${stateKey}-${id}`]
        ? validationErrors[`${stateKey}-${id}`][0]
        : null,
    [id, stateKey, validationErrors]
  );

  const validateOnBlur = useCallback(
    async (schema, stateKey, id, value) => {
      const errorKey = `${stateKey}-${id}`;
      try {
        await schema.validateAt(id, { [id]: value });
        const validationErrorsCopy = { ...validationErrors };
        delete validationErrorsCopy[errorKey];
        dispatch(setValidationErrors(validationErrorsCopy));
      } catch (err) {
        dispatch(
          setValidationErrors({
            ...validationErrors,
            [errorKey]: err.errors,
          })
        );
      }
    },
    [dispatch, validationErrors]
  );

  return (
    <Input
      {...props}
      wrapperClassName={wrapperClassName}
      id={id}
      type="text"
      label={translate(`checkout.customerinfo.${id.toLowerCase()}`)}
      inputState={errorMessage ? 'error' : 'default'}
      message={errorMessage}
      onChange={(event) =>
        onCustomerDetailsChange &&
        onCustomerDetailsChange(stateKey, id, event.target.value)
      }
      onBlur={(e) =>
        validateOnBlur(validationSchema, stateKey, id, e.target.value)
      }
      inputSize="large"
      value={(payload[stateKey] && payload[stateKey][id]) ?? ''}
    />
  );
};

const PrivateCustomerInfo = ({ showSignUp }) => {
  const dispatch = useDispatch();
  const { payload } = useTypedSelector((state) => state.checkout);
  const {
    authenticated,
    customerDetails = {},
    signUp,
    loginUrl,
    showAlternativeAddress,
  } = payload;
  const classes: any = {};

  const onCustomerDetailsChange = useCallback(
    (stateKey, id, value) => {
      switch (stateKey) {
        case 'customerDetails':
          dispatch(setCustomerDetails(id, value));
          break;
        case 'alternativeAddress':
          dispatch(setAlternativeAddress(id, value));
          break;
      }
    },
    [dispatch]
  );

  const titleRight = useMemo(
    () =>
      !authenticated ? (
        <StyledLoginLink>
          {translate('checkout.customerinfo.existingcustomer')}{' '}
          <a href={loginUrl}>{translate('checkout.customerinfo.login')}</a>
        </StyledLoginLink>
      ) : undefined,
    [authenticated, loginUrl]
  );

  return (
    <StyledPrivateCustomerWrapper
      title={translate('checkout.customerinfo.title')}
      titleRight={titleRight}
    >
      <CheckoutSectionContent>
        <form className="section-content">
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="firstName"
            autoComplete="given-name"
            name="fname"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="lastName"
            autoComplete="family-name"
            name="lname"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-fullCol"
            stateKey="customerDetails"
            id="address"
            autoComplete="address-line1"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-fullCol"
            stateKey="customerDetails"
            id="careOf"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="zipCode"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="city"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <div className="checkout__input-fullCol">
            <label className="form__label" htmlFor="country">
              <b>{translate('checkout.customerinfo.country')}</b>
            </label>
            <Dropdown
              selectedValue={customerDetails.country}
              options={global[
                'LitiumConstants'
              ].countries.map(({ text, value }) => ({ label: text, value }))}
              onChange={(value) => {
                onCustomerDetailsChange('alternativeAddress', 'country', value);
                onCustomerDetailsChange('customerDetails', 'country', value);
              }}
            />
          </div>
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="phoneNumber"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CustomerInfoInput
            wrapperClassName="checkout__input-oneCol"
            stateKey="customerDetails"
            id="email"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
        </form>
      </CheckoutSectionContent>
      <CheckoutSectionFooter className={classes.sectionFooter}>
        {showSignUp && (
          <Checkbox
            wrapperClassName="checkout__checkbox"
            id="signupandlogin"
            inputSize="large"
            checked={signUp}
            label={translate('checkout.customerinfo.signupandlogin')}
            onChange={(e) => dispatch(setSignUp(e.target.checked))}
          />
        )}
        <Checkbox
          wrapperClassName="checkout__checkbox"
          id="showAlternativeAddress"
          inputSize="large"
          checked={showAlternativeAddress}
          label={translate('checkout.customerinfo.showAlternativeAddress')}
          onChange={(e) =>
            dispatch(setShowAlternativeAddress(e.target.checked))
          }
        />
      </CheckoutSectionFooter>
      {showAlternativeAddress && (
        <CheckoutSectionContent>
          <form className="section-content">
            <CustomerInfoInput
              wrapperClassName="checkout__input-oneCol"
              stateKey="alternativeAddress"
              id="firstName"
              autoComplete="given-name"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="checkout__input-oneCol"
              stateKey="alternativeAddress"
              id="lastName"
              autoComplete="family-name"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="checkout__input-fullCol"
              stateKey="alternativeAddress"
              id="address"
              autoComplete="address-line1"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="checkout__input-fullCol"
              stateKey="alternativeAddress"
              id="careOf"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="checkout__input-oneCol"
              stateKey="alternativeAddress"
              id="zipCode"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CustomerInfoInput
              wrapperClassName="checkout__input-oneCol"
              stateKey="alternativeAddress"
              id="city"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
          </form>
        </CheckoutSectionContent>
      )}
    </StyledPrivateCustomerWrapper>
  );
};

export default PrivateCustomerInfo;
